import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import StripTags from 'striptags';

import { Carousel, CarouselProvider } from '$partials/base/carousel.jsx';
import { CarouselControls } from '$partials/ui';
import { Columns } from '$partials/layout/columns.jsx';
import { Dispatcher as ItemDispatcher } from '$partials/listings';
import Utils from '$utilities';
import { StaticTitle } from '$partials/layout/side-titles.jsx';

/* =========================================================================  */
/* Component(s) */
/* =========================================================================  */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const ExploreJob = ({
    jobIds        = null,
    locationNames = null,
    sideTitle     = null,
    title         = null
}) => {
    // state
    const [_jobIds] = useState(jobIds?.split(',')?.filter(Boolean) || []);
    const [_locationNames] = useState(locationNames?.split(',')?.filter(Boolean) || []);
    const [jobItems, setJobItems] = useState({});

    // hooks
    useEffect(() => {
        const fetchData = async () => {
            // get job items
            const response = await fetch('/api/v1/joblist/jobs', {
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': Utils.cookies.getItem('crumb')
                },
                method: 'GET',
            });

            if (response?.status !== 200) {
                throw new Error(response?.statusText);
            }

            // get json response
            const responseJson = await response.json();

            // add item type key and filter results
            const mutatedResults = [];
            responseJson.results?.forEach((item, idx) => {
                // add typename to items for item dispatcher
                responseJson.results[idx].__typename = 'Job';

                if (_jobIds.length > 0) {
                    // filter jobs by cms specified job ids
                    if (_jobIds.includes(item?.jobDbId?.toString())) {
                        mutatedResults.push(item);
                    }
                } else if (_locationNames.length > 0) {
                    // filter jobs by cms specified location names
                    const flag = _locationNames.some((location) => {
                        return item?.geo_name?.toLowerCase().includes(location.toLowerCase());
                    });

                    if (flag) {
                        mutatedResults.push(item);
                    }
                } else {
                    // if no filters specified return first 4 items
                    if (idx < 4) {
                        mutatedResults.push(item);
                    }
                }
            });

            responseJson.results = mutatedResults;

            setJobItems(responseJson?.results);
        };

        fetchData();
    }, []);


    // render
    return (
        <CarouselProvider>
            <section className='module-explore-job'>
                <div className='pr-gutter mxn-gutter'> {/* outer grid gutter */}
                    <div className='flex flex-wrap'>
                        {/* side title */}
                        <div className='absolute top-0 md-relative md-col-2'>
                            {sideTitle && (
                                <div className='px-gutter'>
                                    <p className='type-style-5 side-title'>
                                        <StaticTitle title={sideTitle} />
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* content / title */}
                        <div className='col-11 sm-col-8 md-col-7'>
                            <div className='ml9 sm-ml9 md-m0'>
                                <div className='px-gutter'>
                                    <h3>
                                        <div
                                            className='rich-text type-style-1'
                                            dangerouslySetInnerHTML={{ __html: StripTags(title, ['mark', 'a', 'h2', 'h3', 'h4', 'hr', 'br', 'b', 'i']) }} />
                                    </h3>
                                </div>
                            </div>
                        </div>

                        {/* carousel controls */}
                        <div className='md-hide col-12 sm-col-4 md-col-4 self-end'>
                            <div className='ml9 sm-ml9 md-m0 mt6 sm-mt6 md-mt0'>
                                <CarouselControls />
                            </div>
                        </div>
                    </div>
                </div>

                {/* content: mobile/tablet */}
                <div className='md-hide pr-gutter mxn-gutter'> {/* outer grid gutter */}
                    <div className='flex flex-wrap'>
                        {/* side title */}
                        <div className='absolute top-0 md-relative md-col-2'>
                            {(!title && sideTitle) && (
                                <div className='px-gutter'>
                                    <p className='type-style-5 side-title side-title--no-top'>
                                        <StaticTitle title={sideTitle} />
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* content */}
                        <div className='col-12 sm-col-12 md-col-10'>
                            <div className='module-carousel-related__carousel ml9 sm-ml9 md-m0'>
                                {jobItems?.length > 0 && (
                                    <Carousel
                                        items={jobItems?.map((item, idx) => {
                                            return (
                                                <ItemDispatcher
                                                    item={item}
                                                    key={idx} />
                                            );
                                        })}
                                        slideClassName='col-10 sm-col-6 px-gutter pt-gutter'
                                        swiperConfig={{
                                            simulateTouch: true,
                                            slidesPerView: 'auto'
                                        }} />
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* content: desktop */}
                <div className='md-show pr-gutter mxn-gutter'> {/* outer grid gutter */}
                    <div className='flex flex-wrap'>
                        {/* side title */}
                        <div className='absolute top-0 md-relative md-col-2'>
                            {(!title && sideTitle) && (
                                <div className='px-gutter'>
                                    <p className='type-style-5 side-title side-title--no-top'>
                                        {sideTitle}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* content */}
                        <div className='col-12 sm-col-12 md-col-10'>
                            <div className='ml9 sm-ml9 md-ml0'>
                                {jobItems?.length > 0 && (
                                    <section
                                        aria-label='Jobs'
                                        className='flex flex-wrap'
                                        role='feed'>
                                        <Columns
                                            count={2}
                                            items={jobItems?.map((item, idx) => {
                                                return (
                                                    <React.Fragment key={idx}>
                                                        <article
                                                            aria-label={item?.externalPostingName}
                                                            aria-posinset={idx}
                                                            aria-setsize={jobItems.length}
                                                            className='col-12'
                                                            tabIndex={-1}>
                                                            <div className='px-gutter pt-gutter'>
                                                                <ItemDispatcher
                                                                    item={item}
                                                                    key={idx} />
                                                            </div>
                                                        </article>
                                                    </React.Fragment>
                                                );
                                            })} />
                                    </section>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </CarouselProvider>
    );
};

/* =========================================================================  */
/* Typing and exports */
/* =========================================================================  */

const propTypes = {
    jobIds       : PropTypes.string,
    locationNames: PropTypes.string,
    sideTitle    : PropTypes.string,
    title        : PropTypes.string,
};

ExploreJob.propTypes = propTypes;

export { ExploreJob };

export default ExploreJob;
